import clsx from 'clsx';
import * as React from 'react';
import { useObjectRef } from 'react-aria';

import { useEventCallback, useLoadingState } from '@gitbook/util-react-hooks';

import { Description } from '../Description';
import { FieldGroup, Label, useFieldProps } from '../Form';
import { Group } from '../Group';
import { type IconDefinition, renderIcon } from '../util';
import styles from './Switch.module.css';

export type SwitchProps = {
    /**
     * Optional label which can be clicked to trigger the switch.
     * Increasing the hit area for triggering the ui.
     * @deprecated Use `FieldGroup` and `Label` components instead.
     */
    label?: React.ReactNode;

    /**
     * Optional icon displayed before the label
     * @deprecated Use `FieldGroup` and `Group` components instead.
     */
    labelIcon?: IconDefinition;

    /**
     * Optional description
     * @deprecated Use `FieldGroup` and `Description` components instead.
     */
    labelDescription?: React.ReactNode;

    /**
     * Optional badge displayed after the label
     * @deprecated Use `FieldGroup` and `Group` components instead.
     */
    badge?: React.ReactNode;

    /**
     * The value (true/false) that corresponds to the state of the switch.
     */
    value?: boolean;

    /**
     * Whether the switch is disabled. A disabled switch can not be updated.
     */
    disabled?: boolean;

    /**
     * Whether the switch is in a loading state
     */
    loading?: boolean;

    /**
     * Callback triggered when the value of the switch changes.
     * @param value
     */
    onValueChange?: (value: boolean) => void | Promise<void>;

    /**
     * Optional custom styles that will be passed to the root of the
     * component.
     */
    className?: string;

    /**
     * Set a unique ID that can be used for testing purposes.
     */
    testID?: string;

    /**
     * Visual style of the switch.
     * Defaults to `secondary`.
     */
    kind?: 'secondary' | 'upgrade';
};

/**
 * Switch - an interactive UI for toggling things on and off.
 *
 * A switch is composed of two parts - the track and the thumb.
 *
 * A switch can be either on or off and it has a corresponding value
 * that is either true or false.
 *
 * Can have a loading state by setting the `loading` prop to true.
 */
export function Switch(props: SwitchProps) {
    const {
        label,
        labelIcon,
        labelDescription,
        value,
        loading,
        disabled = false,
        onValueChange,
        badge,
        className,
        testID,
        kind = 'secondary',
        ...rest
    } = props;

    const controlProps = {
        value,
        onValueChange,
        testID,
        loading,
        disabled,
        kind,
    };

    if (!label && !labelDescription) {
        return <SwitchControl {...controlProps} className={className} />;
    }

    return (
        <FieldGroup orientation="horizontal" className={className} {...rest}>
            <Group>
                {label ? (
                    <Group>
                        {labelIcon ? renderIcon(labelIcon, { size: 18 }) : null}
                        <Label>{label}</Label>
                        {badge}
                    </Group>
                ) : null}
                {labelDescription && <Description>{labelDescription}</Description>}
            </Group>
            <SwitchControl
                value={value}
                onValueChange={onValueChange}
                testID={testID}
                loading={loading}
                disabled={disabled}
                kind={kind}
            />
        </FieldGroup>
    );
}

const SwitchControl = React.forwardRef(function SwitchControl(
    props: Omit<SwitchProps, 'label' | 'labelIcon' | 'labelDescription' | 'badge'>,
    forwardedRef: React.ForwardedRef<HTMLInputElement>
) {
    const {
        value: propValue,
        loading: propLoading = false,
        disabled = false,
        onValueChange,
        className,
        testID,
        kind,
        ...rest
    } = props;

    const [stateLoading, wrapStateLoading] = useLoadingState();
    const inputRef = useObjectRef(forwardedRef);

    const { ref, id, ...fieldProps } = useFieldProps({ ref: inputRef });
    const handleChange = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const result = onValueChange?.(event.target.checked);
        if (result instanceof Promise) {
            wrapStateLoading(result);
        }
    });
    const value = propValue ?? fieldProps.value;
    const loading = propLoading || stateLoading;
    const state = loading
        ? 'loading'
        : disabled && value
          ? 'checked-disabled'
          : disabled
            ? 'disabled'
            : value
              ? 'checked'
              : undefined;

    return (
        <div
            className={clsx(
                styles.track,
                kind === 'upgrade' && styles.upgrade,
                'peer/switch',
                className
            )}
            data-state={state}
            aria-busy={loading}
            aria-live="polite"
            {...rest}
        >
            <input
                type="checkbox"
                role="switch"
                aria-checked={value}
                data-testid={testID}
                ref={ref}
                className={styles.input}
                id={id}
                checked={value}
                disabled={disabled ?? loading}
                onChange={handleChange}
                {...fieldProps}
            />
            <div className={clsx('size-8', styles.thumb)} />
        </div>
    );
});
